import { fileUpload } from 'api/service.request'
import { PostDataType, postOffensive, postSendAlarm, updateChannel } from 'api/socket.request'
import { useChannel } from 'components/chat/ChannelProvider'
import { useTalkPlusClient } from 'contexts/TalkPlusClientContext'
import dayjs from 'dayjs'
import {
    ChatPayload,
    ChatSendRetryDataType,
    ChatStatus,
    CommentStatus,
    CommentType,
    CustomComment,
    OffensiveData,
} from 'interfaces/talkplus.interface'
import { getForbiddenWords } from 'api/socket.request'
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { userState } from 'store/user'
import { EventData, MessageTypeEnum } from 'talkplus-sdk'
import { maximumByte } from 'utils/data'
import { useConfirmV2 } from 'utils/helper'
import { v4 as uuidv4 } from 'uuid'
import { useMessages } from './MessagesProvider'
import { ForbiddenWords } from 'interfaces/chat.interface'
import { ErrorHandler } from 'api/config'

interface ChatSendFormContextType {
    content: string
    setContent: (content: string) => void
    imgFile: File | null
    setImgFile: (imgFile: File | null) => void
    handlePaste: (event: React.ClipboardEvent<HTMLTextAreaElement>) => void
    submitHandler: () => void
    handleImgClear: () => void
    isDragging: boolean
    dragRef: React.RefObject<HTMLDivElement>
    textareaRef: React.RefObject<HTMLTextAreaElement>
    fileInputRef: React.RefObject<HTMLInputElement>
    handleFileChange: (
        event:
            | React.ChangeEvent<HTMLInputElement>
            | DragEvent
            | React.ClipboardEvent<HTMLTextAreaElement>
    ) => void
    keydownHandler: (ev: React.KeyboardEvent<HTMLTextAreaElement>) => void
    changeHandler: (ev: React.ChangeEvent<HTMLTextAreaElement>) => void
    contentLimitSize: number
    isChatSending: boolean
}

const ChatSendFormContext = createContext<ChatSendFormContextType | undefined>(undefined)

export const ChatSendFormProvider = ({ children }: { children: React.ReactNode }) => {
    const { client } = useTalkPlusClient()
    const { confirm } = useConfirmV2()
    const {
        messagesContainerScrollToBottom,
        handleCustomComment,
        handleTotalImage,
        handleComments,
        errorCommentDeleteHandler,
        handleImageFirstRender,
    } = useMessages()
    const { channel, channelId, debouncedMarkAsRead, updateChannelInfo } = useChannel()
    const user = useRecoilValue(userState)
    // textarea 요소에 대한 참조. DOM 요소와 직접 상호작용할 때 유용함
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    // textarea 또는 댓글 입력 필드의 내용을 관리하는 상태
    const [content, setContent] = useState<string>('')
    const [forbiddenWords, setForbiddenWords] = useState<ForbiddenWords | null>(null)

    // 파일 인풋 ref
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    // 선택된 이미지
    const [imgFile, setImgFile] = useState<File | null>(null)
    // 채팅 전송중 여부
    const [isChatSending, setIsChatSending] = useState(false)

    // 드래그 중일때와 아닐때의 스타일을 구분하기 위한 state 변수
    const [isDragging, setIsDragging] = useState<boolean>(false)
    // 드래그 이벤트를 감지하는 ref 참조변수 (label 태그에 들어갈 예정)
    const dragRef = useRef<HTMLDivElement | null>(null)

    // 댓글 내용의 최대 허용 크기 (예: 댓글의 문자 수 제한)
    const contentLimitSize = 1000

    // 금칙어 목록 조회
    useEffect(() => {
        const fetchForbiddenWords = async () => {
            try {
                const res = await getForbiddenWords()
                if (res.data.data) {
                    setForbiddenWords(res.data.data)
                }
            } catch (error) {
                // 에러 무시
            }
        }

        void fetchForbiddenWords()
    }, [])

    // 이미지 파일 초기화 핸들러
    const handleImgClear = useCallback(() => {
        setImgFile(null)
        if (textareaRef.current) {
            textareaRef.current.disabled = false
            textareaRef.current.placeholder = '채팅을 입력해주세요.'
            textareaRef.current.focus()
        }
    }, [])

    // 이미지 파일 선택 핸들러
    const handleFileChange = useCallback(
        (
            event:
                | React.ChangeEvent<HTMLInputElement>
                | DragEvent
                | React.ClipboardEvent<HTMLTextAreaElement>
        ) => {
            if (isChatSending) return

            let selectedFile: File | undefined

            // 드래그 앤 드롭 이벤트 처리
            if (event instanceof DragEvent) {
                const files = event.dataTransfer?.files

                if (files && files.length > 1) {
                    // 드래그 앤 드롭에서 파일이 2개 이상 선택된 경우
                    confirm({
                        invisibleCancel: true,
                        description: '파일은 최대 한 장만 업로드 가능합니다.',
                    })
                    return // 처리 중지
                }
                if (files && files.length === 1) {
                    selectedFile = event.dataTransfer.files[0] // 드롭된 파일 가져오기
                }
            }
            // 클립보드 이벤트 처리 (붙여넣기)
            else if ('clipboardData' in event) {
                const files = Array.from(event.clipboardData.items)
                    .filter((item) => item.type.startsWith('image/'))
                    .map((item) => item.getAsFile())
                    .filter((file): file is File => file !== null)

                if (files.length > 1) {
                    // 클립보드에서 이미지가 2개 이상 붙여넣어진 경우
                    confirm({
                        invisibleCancel: true,
                        description: '파일은 최대 한 장만 업로드 가능합니다.',
                    })
                    return // 처리 중지
                }
                if (files.length === 1) {
                    selectedFile = files[0] // 붙여넣어진 이미지 파일 가져오기
                }
            }
            // ChangeEvent 처리 (파일 입력)
            else if (event.target && 'files' in event.target) {
                const files = event.target.files
                if (files && files.length === 1) {
                    selectedFile = files[0] // 선택된 파일 가져오기
                }
            }

            if (!selectedFile || !fileInputRef.current) return

            // 선택 파일이 최대 용량보다 크다면 알림 표시
            if (maximumByte < selectedFile.size) {
                // 경고창 표시
                confirm({
                    invisibleCancel: true,
                    description: '파일은 10MB 이하로 등록해주세요.',
                })
                // 선택 이미지 제거
                handleImgClear()
                fileInputRef.current.value = ''
                return
            }

            if (selectedFile.type.startsWith('image/')) {
                setImgFile(selectedFile)
                setContent('')
                if (textareaRef.current) {
                    textareaRef.current.disabled = true
                    textareaRef.current.placeholder = '이미지 선택시 채팅 입력이 불가합니다.'
                }
            } else {
                confirm({
                    invisibleCancel: true,
                    description: '이미지 파일만 선택 가능합니다.',
                })
            }

            fileInputRef.current.value = ''
        },
        [confirm, handleImgClear, isChatSending]
    )

    const maxHeightUpdate = (element: HTMLTextAreaElement) => {
        textareaRef.current?.style.setProperty('height', 'inherit')
        const { scrollHeight } = element
        textareaRef.current?.style.setProperty('height', scrollHeight + 'px')
    }

    // clearHandler: 텍스트 영역을 초기화하고 스크롤을 최하단으로 이동시키는 함수
    const clearHandler = useCallback(() => {
        // content 상태를 빈 문자열로 설정
        setContent('')
        // scrollRef.current의 scrollHeight를 사용하여 스크롤을 최하단으로 이동
        messagesContainerScrollToBottom()
        // 텍스트 영역의 값을 빈 문자열로 설정
        textareaRef.current!.value = ''
        // 텍스트 영역의 높이를 기본값인 64px로 설정
        textareaRef.current?.style.setProperty('height', '64px')
    }, [messagesContainerScrollToBottom])

    // changeHandler: 텍스트 영역의 내용이 변경될 때 호출되는 함수
    const changeHandler = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        // 입력된 텍스트의 길이가 100자를 초과하는 경우
        if (ev.target.value.length > 100) {
            // contentLimitSize까지의 문자열로 잘라서 상태를 업데이트
            const truncatedText = ev.target.value.substring(0, contentLimitSize)
            setContent(truncatedText)
            return // 더 이상 진행하지 않음
        }

        // maxHeightUpdate: 텍스트 영역의 높이를 업데이트하는 함수 호출
        maxHeightUpdate(ev.target)
        // content 상태를 텍스트 영역의 현재 값으로 설정
        setContent(ev.target.value)
    }

    // 채널 상태 진행중 핸들러
    const statusInProgressHandler = useCallback(async () => {
        // 채널 상태가 진행중이 아닌 경우에만 진행중으로 변경
        if (channel && channel.category !== ChatStatus.IN_PROGRESS) {
            updateChannel(channelId, { category: ChatStatus.IN_PROGRESS })
        }
    }, [channel, channelId])

    const createChatPayload = useCallback(
        (chatSendRetryData?: ChatSendRetryDataType): ChatPayload => {
            return {
                id: uuidv4(),
                text: chatSendRetryData?.text || content,
                type: MessageTypeEnum.Text,
                data: {
                    imageURL: chatSendRetryData?.data.imageURL || '', // 이미지 업로드 후 이미지 주소 저장
                    file: imgFile || null,
                },
                userId: String(user?.userId),
                createdAt: dayjs().valueOf(),
            }
        },
        [content, imgFile, user?.userId]
    )

    // 채팅 전송 시 이미지 서버 업로드
    const imageUpload = useCallback(async () => {
        if (!imgFile) return ''
        const formData = new FormData()
        formData.append('files', imgFile)
        const imgURLRes = await fileUpload(formData)
        if (imgURLRes.data && imgURLRes.data.data && imgURLRes.data.data.length > 0) {
            return imgURLRes.data.data[0].url
        }
        return ''
    }, [imgFile])

    const talkPlusChatSend = useCallback(
        async (basicChatPayload: ChatPayload) =>
            await client.sendMessage({
                channelId,
                text: basicChatPayload.data.imageURL ? '' : basicChatPayload.text || content,
                type: MessageTypeEnum.Text,
                data: { imageURL: basicChatPayload.data.imageURL },
            }),
        [client, channelId, content]
    )

    // 클릭메이트 채팅 전송
    const CMChatSend = useCallback(
        async ({ userId, basicChatPayload }: { userId: string; basicChatPayload: ChatPayload }) => {
            if (!user) return

            const isImage = Boolean(basicChatPayload.data.imageURL)

            const cmChatPayload: PostDataType = {
                message: {
                    type: isImage ? 'IMAGE' : 'TEXT',
                    body: isImage ? '[이미지]' : basicChatPayload.text,
                },
                additionalData: {
                    chatRoomId: String(user.userId),
                    clickId: String(user.userId),
                    sendUserName: user.name,
                    profileUrl: user.profileUrl || '',
                    fileUrl: basicChatPayload.data.imageURL || '',
                    talkplusMessageId: basicChatPayload.id,
                    createdAt: basicChatPayload.createdAt,
                },
            }

            await postSendAlarm(userId, cmChatPayload)
        },
        [user]
    )

    function convertStringToRegex(raw: string): RegExp | null {
        // "/" 로 둘러싸인 패턴과 플래그를 캡처하기 위한 정규식
        const match = raw.match(/^\/(.+)\/([gimsuy]*)$/)
        if (!match) {
            return null
        }

        // match[1] → pattern, match[2] → flags
        const [, pattern, flags] = match
        try {
            return new RegExp(pattern, flags)
        } catch {
            return null
        }
    }

    const checkForbiddenWords = useCallback(
        (text: string): boolean => {
            if (!forbiddenWords) return true
            // patterns 검사
            const hasForbiddenPattern = forbiddenWords.patterns.some((pattern) => {
                const regex = convertStringToRegex(pattern)

                if (!regex) return false
                return regex.test(text)
            })

            if (hasForbiddenPattern) {
                return true
            }

            // combos 검사
            const hasForbiddenCombo = forbiddenWords.combos.some((combo) => {
                return combo.every((word) => text.toLowerCase().includes(word.toLowerCase()))
            })

            return hasForbiddenCombo
        },
        [forbiddenWords]
    )

    // 채팅 전송 함수
    const submitHandler = useCallback(
        async (chatSendRetryData?: ChatSendRetryDataType) => {
            // 채널이 없거나 사용자 정보가 없는 경우 채팅 전송하지 않음
            if (!channel || !user || !channelId) return

            // 채팅이 전송중인 경우 얼리 리턴
            if (isChatSending) {
                confirm({
                    invisibleCancel: true,
                    description: '채팅을 전송중입니다.',
                })
                return
            }

            // 전송할 채팅 데이터가 없다면 경고 메시지를 표시하고 인풋 포커스
            if (!textareaRef.current!.value && !imgFile && !chatSendRetryData) {
                confirm({
                    invisibleCancel: true,
                    description: '채팅내용을 작성해주세요.',
                    onConfirm() {
                        textareaRef.current?.focus()
                    },
                })
                textareaRef.current?.blur() // 텍스트 영역의 포커스를 해제
                return // 더 이상 진행하지 않음
            }

            // 채팅 전송 중 상태 변경
            setIsChatSending(true)

            // 채팅 전송 페이로드 생성
            const basicChatPayload = createChatPayload(chatSendRetryData)
            clearHandler()

            // 최초 채팅 전송 시 필요한 서버 이미지 업로드
            if (!chatSendRetryData) {
                try {
                    basicChatPayload.data.imageURL = await imageUpload()
                } catch (err) {
                    confirm({
                        invisibleCancel: true,
                        description: '이미지 업로드에 실패하였습니다.',
                    })
                    setIsChatSending(false)
                    return
                }
            }
            // 채팅 메시지 전송 mutation 호출

            try {
                if (checkForbiddenWords(basicChatPayload.text)) {
                    // 채팅 전송 전, 금칙어 체크 => 강제 forbidden 에러 발생
                    throw new ErrorHandler('500012', '금칙어입니다.')
                }
                const res = await talkPlusChatSend(basicChatPayload)
                // 전송된 채팅 이미지 여부
                const isImage = Boolean(basicChatPayload.data.imageURL)
                // 클메 채팅 전송
                await CMChatSend({
                    userId: channel.guest.id,
                    basicChatPayload,
                })

                handleCustomComment(res.message, true, CommentStatus.SUCCESS)
                // 채널 상태 변경
                statusInProgressHandler()

                if (isImage) {
                    // 이미지 전송 시 총 렌더링 이미지 개수 증가
                    handleTotalImage((prev) => (prev ? prev + 1 : 1))
                    // 이미지를 전송하면 해당 이미지가 로드되고 나서 하단으로 스크롤하기 위해 imageFirstRender 상태 변경
                    handleImageFirstRender(true)
                }

                // 메세지 전송 성공 후 읽음 처리
                debouncedMarkAsRead(channel.id)

                // 이전에 실패했던 채팅을 재전송으로 성공했다면, 이전 실패 채팅 삭제
                if (chatSendRetryData?.status === CommentStatus.ERROR) {
                    errorCommentDeleteHandler(chatSendRetryData.id)
                }
                messagesContainerScrollToBottom()
            } catch (err) {
                const errorChatData: CustomComment = {
                    id: uuidv4(),
                    type: CommentType.TEXT,
                    text: basicChatPayload.text,
                    data: { imageURL: basicChatPayload.data.imageURL },
                    userId: channel.seller.id,
                    createdAt: Date.now(),
                    channelId,
                    username: channel.seller.id,
                    status: CommentStatus.ERROR,
                }

                if (
                    typeof err === 'object' &&
                    err !== null &&
                    'code' in err &&
                    (err.code === '500012' || err.code === '4004')
                ) {
                    errorChatData.status = CommentStatus.FORBIDDEN

                    // 서버에 금칙어 오류 등록
                    const offensiveData: OffensiveData = {
                        chatRoomId: channelId,
                        sendUserId: Number(channel.seller.id),
                        receiveUserId: Number(channel.guest.id),
                        profileUrl: channel.seller.profileImageUrl,
                        nickname: channel.seller.username,
                        content: basicChatPayload.text || content,
                        sentAt: Date.now(),
                    }

                    postOffensive(offensiveData)

                    if (chatSendRetryData) {
                        // 만약 네트워크 오류로 실패했던것이라 재전송 되었다면, 해당 메세지의 타입을 Forbidden으로 변경
                        handleComments((prevComments) =>
                            prevComments.map((comment) =>
                                comment.id === chatSendRetryData.id
                                    ? {
                                          ...comment,
                                          status: CommentStatus.FORBIDDEN,
                                      }
                                    : comment
                            )
                        )
                    }
                }

                // 앞전에 실패했던 채팅이 재전송하여 또 실패한 것이라면, 추가하지 않음
                if (chatSendRetryData?.status !== CommentStatus.ERROR) {
                    handleComments((prevComments) => [...prevComments, errorChatData])
                }

                messagesContainerScrollToBottom()
            } finally {
                handleImgClear()
                // 이미지 전송 상태 초기화
                setIsChatSending(false)
            }
            // 톡플러스 채팅 전송
        },
        [
            CMChatSend,
            isChatSending,
            talkPlusChatSend,
            messagesContainerScrollToBottom,
            imgFile,
            channelId,
            confirm,
            content,
            createChatPayload,
            imageUpload,
            clearHandler,
            statusInProgressHandler,
            channel,
            debouncedMarkAsRead,
            handleCustomComment,
            handleTotalImage,
            handleComments,
            errorCommentDeleteHandler,
            user,
            handleImageFirstRender,
            handleImgClear,
            checkForbiddenWords,
        ]
    )

    const keydownHandler = (ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (ev.nativeEvent.isComposing) return

        if (
            (ev.shiftKey && ev.key === 'Enter') ||
            (window.innerWidth <= 768 && ev.key === 'Enter')
        ) {
            return // PC에서는 shift+enter, 모바일에서는 enter로 줄바꿈 가능
        }
        if (ev.ctrlKey && ev.key.toUpperCase() === 'ENTER') {
            const target = ev.target as HTMLTextAreaElement

            if (target.value.length >= contentLimitSize) return

            maxHeightUpdate(target)

            const front = target.value.substring(0, target.selectionStart)
            const remain = target.value.substring(target.selectionStart, target.value.length)

            textareaRef.current!.value = front + '\n' + remain
            textareaRef.current!.selectionStart = front.length + 1
            textareaRef.current!.selectionEnd = front.length + 1
            return
        }

        if (ev.key.toUpperCase() === 'ENTER') {
            ev.preventDefault()
            submitHandler()
            return
        }
    }

    const handleEvent = useCallback(
        async (data: EventData) => {
            // 메시지를 받을 경우 채팅 리스트 재 렌더링
            if (data.type === 'message' && data.channel && data.message) {
                // 채팅 이벤트가 수신된 채팅창에 접속 중이라면 채팅 읽음 처리
                if (channelId === data.channel.id) {
                    handleCustomComment(data.message, true, CommentStatus.SUCCESS)
                    debouncedMarkAsRead(channelId)
                    messagesContainerScrollToBottom()
                    if (data.message.data?.imageURL) {
                        // 수신한 채팅이 이미지라면 아래로 스크롤
                        handleTotalImage((prev) => (prev ? prev + 1 : 1))
                        handleImageFirstRender(true)
                    }
                }
            }

            if (
                data.type === 'channelChanged' &&
                data.channel &&
                data.channel.members &&
                channelId === data.channel.id
            ) {
                await updateChannelInfo(data.channel)
            }
        },
        [
            channelId,
            debouncedMarkAsRead,
            updateChannelInfo,
            handleTotalImage,
            messagesContainerScrollToBottom,
            handleCustomComment,
            handleImageFirstRender,
        ]
    )

    // 이벤트 등록
    useEffect(() => {
        client.on('event', handleEvent)
        return () => {
            client.off('event', handleEvent)
        }
    }, [client, handleEvent])

    const handleDragIn = useCallback((e: DragEvent): void => {
        e.preventDefault()
        e.stopPropagation()
    }, [])

    const handleDragOut = useCallback((e: DragEvent): void => {
        e.preventDefault()
        e.stopPropagation()

        setIsDragging(false)
    }, [])

    const handleDragOver = useCallback((e: DragEvent): void => {
        e.preventDefault()
        e.stopPropagation()

        if (e.dataTransfer!.files) {
            setIsDragging(true)
        }
    }, [])

    const handleDrop = useCallback(
        (e: DragEvent): void => {
            e.preventDefault()
            e.stopPropagation()

            handleFileChange(e)

            setIsDragging(false)
        },
        [handleFileChange]
    )

    const initDragEvents = useCallback((): void => {
        // 앞서 등록된 4개의 이벤트에 Listener를 등록합니다. (마운트 될때)

        if (dragRef.current !== null && !isChatSending) {
            dragRef.current.addEventListener('dragenter', handleDragIn)
            dragRef.current.addEventListener('dragleave', handleDragOut)
            dragRef.current.addEventListener('dragover', handleDragOver)
            dragRef.current.addEventListener('drop', handleDrop)
        }
    }, [handleDragIn, handleDragOut, handleDragOver, handleDrop, isChatSending])

    const resetDragEvents = useCallback((): void => {
        // 앞서 등록된 4개의 이벤트에 Listener를 삭제합니다. (언마운트 될때)

        if (dragRef.current !== null) {
            dragRef.current.removeEventListener('dragenter', handleDragIn)
            dragRef.current.removeEventListener('dragleave', handleDragOut)
            dragRef.current.removeEventListener('dragover', handleDragOver)
            dragRef.current.removeEventListener('drop', handleDrop)
        }
    }, [handleDragIn, handleDragOut, handleDragOver, handleDrop])

    const handleEnterImg = useCallback(
        (e: KeyboardEvent) => {
            // 엔터 키가 눌렸을 때 이미지 파일이 있으면 이미지를 전송합니다
            if (e.key === 'Enter' && imgFile) {
                submitHandler()
            }
        },
        [imgFile, submitHandler]
    )

    useEffect(() => {
        initDragEvents()
        window.addEventListener('keydown', handleEnterImg)

        return () => {
            window.removeEventListener('keydown', handleEnterImg)
            resetDragEvents()
        }
    }, [initDragEvents, resetDragEvents, handleEnterImg])

    const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const clipboardData = event.clipboardData
        const items = clipboardData.items
        const type = items[0].type

        if (type.startsWith('text/')) return // 텍스트 / html 붙여넣기라면 얼리 리턴하여 기본 동작 실행

        if (items) {
            event.preventDefault() // 기본 붙여넣기 동작 방지
            handleFileChange(event)
        }
    }

    return (
        <ChatSendFormContext.Provider
            value={{
                content,
                setContent,
                imgFile,
                setImgFile,
                handlePaste,
                submitHandler,
                handleImgClear,
                isDragging,
                dragRef,
                textareaRef,
                fileInputRef,
                handleFileChange,
                keydownHandler,
                changeHandler,
                contentLimitSize,
                isChatSending,
            }}
        >
            {children}
        </ChatSendFormContext.Provider>
    )
}

export const useChatSendForm = () => {
    const context = useContext(ChatSendFormContext)
    if (!context) {
        throw new Error('useChatSendForm must be used within a ChatSendFormProvider')
    }
    return context
}
